import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const fetchRechargeRecords = createAsyncThunk(
  'finance/fetchRechargeRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/finance/recharge', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取充值记录失败');
    }
  }
);

export const fetchWithdrawRecords = createAsyncThunk(
  'finance/fetchWithdrawRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/finance/withdraw', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取提现记录失败');
    }
  }
);

export const fetchTransactionRecords = createAsyncThunk(
  'finance/fetchTransactionRecords',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/finance/transactions', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取交易记录失败');
    }
  }
);

export const fetchFinanceStatistics = createAsyncThunk(
  'finance/fetchFinanceStatistics',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/finance/statistics', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取财务统计失败');
    }
  }
);

export const fetchTransactionTypes = createAsyncThunk(
  'finance/fetchTransactionTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/admin/finance/transaction-types');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取流水类型失败');
    }
  }
);

export const approveRecharge = createAsyncThunk(
  'finance/approveRecharge',
  async ({ recordId, approved, remark }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/finance/recharge/${recordId}/audit`, { 
        auditResult: approved ? 'approved' : 'rejected',
        auditRemark: remark
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '审核充值失败');
    }
  }
);

export const approveWithdraw = createAsyncThunk(
  'finance/approveWithdraw',
  async ({ recordId, approved, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/finance/withdraw/${recordId}/audit`, { 
        auditResult: approved ? 'approved' : 'rejected',
        auditRemark: reason || '审核通过'
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '审核提现失败');
    }
  }
);

export const processWithdraw = createAsyncThunk(
  'finance/processWithdraw',
  async ({ recordId, transactionId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/finance/withdraw/${recordId}/process`, { 
        transactionId 
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '处理提现失败');
    }
  }
);

export const manualAdjustment = createAsyncThunk(
  'finance/manualAdjustment',
  async ({ userId, amount, type, reason }, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/finance/manual-adjustment', {
        userId,
        amount,
        type,
        reason,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '手动调账失败');
    }
  }
);

// 初始状态
const initialState = {
  rechargeRecords: [],
  withdrawRecords: [],
  transactionRecords: [],
  transactionTypes: [],
  statistics: {
    totalRecharge: 0,
    totalWithdraw: 0,
    totalTransactions: 0,
    netInflow: 0,
    pendingRecharge: 0,
    pendingWithdraw: 0,
    successRate: 0,
  },
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {},
  isLoading: false,
  error: null,
};

// slice
const financeSlice = createSlice({
  name: 'finance',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取充值记录
      .addCase(fetchRechargeRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchRechargeRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rechargeRecords = action.payload.records;
        // 只更新分页的关键字段，避免对象引用变化导致无限循环
        if (action.payload.pagination) {
          state.pagination = {
            ...state.pagination,
            current: action.payload.pagination.current || action.payload.pagination.page || state.pagination.current,
            pageSize: action.payload.pagination.pageSize || action.payload.pagination.limit || state.pagination.pageSize,
            total: action.payload.pagination.total || 0,
            pages: action.payload.pagination.pages || 0,
          };
        }
        state.error = null;
      })
      .addCase(fetchRechargeRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取提现记录
      .addCase(fetchWithdrawRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.withdrawRecords = action.payload.records;
        // 只更新分页的关键字段，避免对象引用变化导致无限循环
        if (action.payload.pagination) {
          state.pagination = {
            ...state.pagination,
            current: action.payload.pagination.current || action.payload.pagination.page || state.pagination.current,
            pageSize: action.payload.pagination.pageSize || action.payload.pagination.limit || state.pagination.pageSize,
            total: action.payload.pagination.total || 0,
            pages: action.payload.pagination.pages || 0,
          };
        }
        state.error = null;
      })
      .addCase(fetchWithdrawRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取交易记录
      .addCase(fetchTransactionRecords.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTransactionRecords.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transactionRecords = action.payload.records;
        // 只更新分页的关键字段，避免对象引用变化导致无限循环
        if (action.payload.pagination) {
          state.pagination = {
            ...state.pagination,
            current: action.payload.pagination.current || action.payload.pagination.page || state.pagination.current,
            pageSize: action.payload.pagination.pageSize || action.payload.pagination.limit || state.pagination.pageSize,
            total: action.payload.pagination.total || 0,
            pages: action.payload.pagination.pages || 0,
          };
        }
        state.error = null;
      })
      .addCase(fetchTransactionRecords.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 获取财务统计
      .addCase(fetchFinanceStatistics.fulfilled, (state, action) => {
        state.statistics = action.payload;
      })
      // 获取流水类型
      .addCase(fetchTransactionTypes.fulfilled, (state, action) => {
        state.transactionTypes = action.payload;
      })
      // 审核充值
      .addCase(approveRecharge.fulfilled, (state, action) => {
        const index = state.rechargeRecords.findIndex(record => record.id === action.payload.id);
        if (index !== -1) {
          state.rechargeRecords[index] = action.payload;
        }
      })
      // 审核提现
      .addCase(approveWithdraw.fulfilled, (state, action) => {
        const index = state.withdrawRecords.findIndex(record => record.id === action.payload.id);
        if (index !== -1) {
          state.withdrawRecords[index] = action.payload;
        }
      })
      // 处理提现
      .addCase(processWithdraw.fulfilled, (state, action) => {
        const index = state.withdrawRecords.findIndex(record => record.id === action.payload.id);
        if (index !== -1) {
          state.withdrawRecords[index] = action.payload;
        }
      });
  },
});

export const { clearError, setFilters, setPagination } = financeSlice.actions;
export default financeSlice.reducer;
